import { get } from "lodash";
import React, { Component, Fragment } from "react";
import { connect } from "react-redux";
import { PropTypes } from "prop-types";
import { Table, Button } from "semantic-ui-react";
import { Field } from "formik";

import FormikSemanticInput from "../forms/formik/FormikSemanticInput.jsx";
import FormikSemanticDropdown from "../forms/formik/FormikSemanticDropdown.jsx";
import RowActionsMenu from "../forms/tables/RowActionsMenu.jsx";
import ResourceMarker from "../marking/ResourceMarker.jsx";
import LabelWithHelpPopup from "../labelWithHelpPopup/LabelWithHelpPopup.jsx";

import { ROW_STATE } from "../forms/formConstants.js";
import {
  CONNECTION_PROTOCOLS,
  CONNECTION_SCOPES,
  PUBLIC_CONNECTION_SCOPE_KEYS
} from "../../constants/connections.js";

import { selectCopy } from "../../state/reducers/locale/copySelector.js";

class ConnectionRow extends Component {
  static propTypes = {
    data: PropTypes.object,
    mode: PropTypes.string.isRequired,
    onEdit: PropTypes.func,
    onDelete: PropTypes.func,
    onRemove: PropTypes.func.isRequired,
    onReset: PropTypes.func.isRequired,
    formikProps: PropTypes.object.isRequired,
    readOnly: PropTypes.bool.isRequired,
    disabled: PropTypes.bool.isRequired,
    apiErrors: PropTypes.object,
    copyKey: PropTypes.string.isRequired,
    copy: PropTypes.func.isRequired
  };

  static defaultProps = {
    mode: ROW_STATE.VIEW,
    copyKey: "project_settings"
  };

  // -- HANDLERS -- //

  editRow = () => {
    const { id, onEdit } = this.props;
    if (onEdit) onEdit(id);
  };

  deleteRow = () => {
    const { id, onDelete } = this.props;
    if (onDelete) onDelete(id);
  };

  removeRow = () => {
    const { id, onRemove } = this.props;
    onRemove(id);
  };

  resetRow = () => {
    const { id, onReset } = this.props;
    onReset(id);
  };

  // -- RENDER -- //

  renderControls = () => {
    const { mode, onEdit, onDelete, disabled, readOnly, copy } = this.props;

    const onEditFunc = onEdit ? this.editRow : null;
    const onDeleteFunc = onDelete ? this.deleteRow : null;

    // if this is a new connection add a remove row button
    // if this is an existing connection show an dropdown of options
    if (mode === ROW_STATE.NEW) {
      return (
        <Button
          onClick={this.removeRow}
          icon="minus circle"
          size="small"
          floated="right"
          type="button"
          disabled={disabled || readOnly}
        />
      );
    } else {
      return (
        <RowActionsMenu
          onEdit={onEditFunc}
          onDelete={onDeleteFunc}
          onReset={this.resetRow}
          deleting={mode === ROW_STATE.DELETE}
          editing={mode === ROW_STATE.EDIT}
          disabled={disabled || readOnly}
          copy={copy("basic_resource_actions")}
        />
      );
    }
  };

  renderInputForValue = (name, placeholder, help) => {
    const { mode, readOnly, disabled, apiErrors, formikProps } = this.props;

    const value = get(formikProps, `values.${name}`, "");

    // @todo it would be great to drive the editability based upon
    // the fields in the relevant operation
    if (mode === ROW_STATE.NEW) {
      return (
        <Field
          name={name}
          placeholder={placeholder}
          readOnly={readOnly}
          disabled={disabled}
          apiErrors={apiErrors}
          component={FormikSemanticInput}
        />
      );
    } else {
      if (help) {
        return <LabelWithHelpPopup label={value} help={help} />;
      }
      return <span>{value}</span>;
    }
  };

  renderInputForDescription = (name, placeholder, help) => {
    const { mode, readOnly, disabled, apiErrors, formikProps } = this.props;

    const value = get(formikProps, `values.${name}`, "");

    // @todo it would be great to drive the editability based upon
    // the fields in the relevant operation
    if (mode === ROW_STATE.EDIT || mode === ROW_STATE.NEW) {
      return (
        <Field
          name={name}
          placeholder={placeholder}
          readOnly={readOnly}
          disabled={disabled}
          apiErrors={apiErrors}
          component={FormikSemanticInput}
        />
      );
    } else {
      if (help) {
        return <LabelWithHelpPopup label={value} help={help} />;
      }
      return <span>{value}</span>;
    }
  };

  renderScopeDropdownHelp = () => {
    const { data, copy } = this.props;

    if (!data || (!data.protocol && !data.port)) return undefined;

    return (
      <p>
        {data.protocol && (
          <Fragment>
            <strong>{copy("connections.protocol")}: </strong>
            {copy(
              `connections.protocols.${CONNECTION_PROTOCOLS[data.protocol]}`
            )}
          </Fragment>
        )}
        {data.protocol && data.port && <br />}
        {data.port && (
          <Fragment>
            <strong>{copy("connections.port")}: </strong>
            {data.port}
          </Fragment>
        )}
      </p>
    );
  };

  renderScopeDropdown = (name, placeholder, help) => {
    const { mode, readOnly, disabled, apiErrors, formikProps, copy } =
      this.props;

    const value = get(formikProps, `values.${name}`, "");

    const scopeOptions = PUBLIC_CONNECTION_SCOPE_KEYS.map(item => {
      return {
        key: CONNECTION_SCOPES[item],
        value: CONNECTION_SCOPES[item],
        text: copy(`connections.scopes.${CONNECTION_SCOPES[item]}`),
        disabled
      };
    });

    //// ignore API only scopes not currently handled in the SPA
    // const editableScope = !!scopeOptions.find(item => item.value === value);
    const editableScope = false;

    // @todo it would be great to drive the editability based upon
    // the fields in the relevant operation
    if ((mode === ROW_STATE.EDIT && editableScope) || mode === ROW_STATE.NEW) {
      return (
        <Field
          name={name}
          placeholder={placeholder}
          readOnly={readOnly}
          options={scopeOptions}
          required={true}
          disabled={disabled}
          apiErrors={apiErrors}
          component={FormikSemanticDropdown}
        />
      );
    } else {
      if (help) {
        return (
          <LabelWithHelpPopup
            label={copy(`connections.scopes.${value}`)}
            help={help}
          />
        );
      }
      return <span>{copy(`connections.scopes.${value}`)}</span>;
    }
  };

  render() {
    const { id, data, mode, copyKey, copy } = this.props;

    return (
      <Table.Row
        key={id}
        textAlign="left"
        verticalAlign="top"
        negative={mode === ROW_STATE.DELETE}
      >
        <Table.Cell>
          {this.renderInputForValue(
            `${id}.value`,
            copy(`${copyKey}.value_placeholder`)
          )}
        </Table.Cell>
        <Table.Cell>
          {this.renderInputForDescription(
            `${id}.description`,
            copy(`${copyKey}.description_placeholder`)
          )}
        </Table.Cell>
        <Table.Cell>
          {this.renderScopeDropdown(
            `${id}.scope`,
            copy(`${copyKey}.scope_placeholder`),
            this.renderScopeDropdownHelp()
          )}
        </Table.Cell>
        <Table.Cell verticalAlign="middle" collapsing>
          {this.renderControls()}
        </Table.Cell>
        <Table.Cell verticalAlign="middle" collapsing>
          {mode !== ROW_STATE.NEW && data && (
            <ResourceMarker
              marking={data.marking}
              circular={true}
              style={{ display: "block" }}
            />
          )}
        </Table.Cell>
      </Table.Row>
    );
  }
}

const mapStateToProps = state => ({
  copy: selectCopy(state)
});

export default connect(mapStateToProps)(ConnectionRow);
